import React, { useState } from "react";
import { Phone, Mail, MapPin, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { api, BUSINESS } from "../../lib/config";

export default function Contact() {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Name, email and message are required.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent. We'll be in touch soon.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      toast.error("Could not send message.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="py-20 lg:py-28" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-[0.9fr_1.1fr] gap-12">
        <div>
          <div className="step-num">07 — GET IN TOUCH</div>
          <h2 className="font-display text-4xl sm:text-5xl font-bold mt-3 tracking-tight">
            Talk to a certified tester today.
          </h2>

          <div className="mt-8 border-l border-t border-slate-200 max-w-md">
            <a
              href={BUSINESS.phoneHref}
              className="p-5 border-r border-b border-slate-200 bg-white flex items-center gap-4 hover:bg-slate-50"
              data-testid="contact-phone-link"
            >
              <div className="w-10 h-10 bg-orange-500 flex items-center justify-center">
                <Phone className="w-5 h-5 text-white" />
              </div>
              <div>
                <div className="overline">Phone</div>
                <div className="font-display font-bold text-xl">{BUSINESS.phone}</div>
              </div>
            </a>
            <a
              href={BUSINESS.emailHref}
              className="p-5 border-r border-b border-slate-200 bg-white flex items-center gap-4 hover:bg-slate-50"
              data-testid="contact-email-link"
            >
              <div className="w-10 h-10 bg-blue-600 flex items-center justify-center">
                <Mail className="w-5 h-5 text-white" />
              </div>
              <div className="min-w-0">
                <div className="overline">Email</div>
                <div className="font-display font-semibold text-sm truncate">{BUSINESS.email}</div>
              </div>
            </a>
            <div className="p-5 border-r border-b border-slate-200 bg-white flex items-center gap-4">
              <div className="w-10 h-10 border border-slate-900 flex items-center justify-center">
                <MapPin className="w-5 h-5 text-slate-900" />
              </div>
              <div>
                <div className="overline">Service Area</div>
                <div className="font-display font-semibold text-sm">
                  Laredo · Zapata · Cotulla · Freer · McAllen · Brownsville
                </div>
              </div>
            </div>
          </div>
        </div>

        <form
          onSubmit={onSubmit}
          className="border border-slate-200 p-6 lg:p-8 bg-white"
          data-testid="contact-form"
        >
          <h3 className="font-display text-2xl font-bold tracking-tight">Send a Message</h3>
          <div className="grid sm:grid-cols-2 gap-4 mt-5">
            <div>
              <label className="field-label">Name *</label>
              <input name="name" value={form.name} onChange={onChange} className="lb-input" required data-testid="contact-name-input" />
            </div>
            <div>
              <label className="field-label">Phone</label>
              <input name="phone" value={form.phone} onChange={onChange} className="lb-input" data-testid="contact-phone-input" />
            </div>
            <div className="sm:col-span-2">
              <label className="field-label">Email *</label>
              <input name="email" type="email" value={form.email} onChange={onChange} className="lb-input" required data-testid="contact-email-input" />
            </div>
            <div className="sm:col-span-2">
              <label className="field-label">Subject</label>
              <input name="subject" value={form.subject} onChange={onChange} className="lb-input" data-testid="contact-subject-input" />
            </div>
            <div className="sm:col-span-2">
              <label className="field-label">Message *</label>
              <textarea name="message" value={form.message} onChange={onChange} rows={4} className="lb-textarea" required data-testid="contact-message-input" />
            </div>
          </div>
          <button
            type="submit"
            disabled={loading}
            className="btn-primary w-full mt-5 justify-center disabled:opacity-60"
            data-testid="contact-submit-btn"
          >
            {loading ? "Sending..." : "Send Message"} <ArrowRight className="w-4 h-4" />
          </button>
        </form>
      </div>
    </section>
  );
}
